import { cn, cva } from 'noddi-ui-common';
import { formatDecimal } from 'noddi-util';
import { Link } from 'react-router-dom';

const changeVariants = cva('shrink-0 text-xs font-semibold whitespace-nowrap', {
  variants: {
    direction: {
      up: 'text-successDark',
      down: 'text-destructive'
    }
  }
});

interface DashboardMetricProps {
  value: string | number;
  label: string;
  /** Percent change against the previous period; the sign picks the color. */
  change?: number;
  /** Text after the change, e.g. "vs prev. 30 days". */
  changeLabel?: string;
  /** Extra node at the end of the row. */
  trailing?: React.ReactNode;
  /** Makes the whole metric a link, e.g. to the list it counts; hovers like the widget's rows. */
  to?: string;
  className?: string;
}

/** The headline number of a dashboard widget, with what it counts underneath. */
export const DashboardMetric = ({
  value,
  label,
  change,
  changeLabel = 'vs prev.',
  trailing,
  to,
  className
}: DashboardMetricProps) => {
  const content = (
    <>
      <div className='flex min-w-0 flex-col gap-1'>
        <span className='text-2xl leading-tight font-semibold tracking-tight tabular-nums'>{value}</span>
        <span className='text-sm text-muted-foreground'>{label}</span>
      </div>
      {change !== undefined && (
        <span className={changeVariants({ direction: change >= 0 ? 'up' : 'down' })}>
          {change >= 0 ? '+' : ''}
          {formatDecimal(change, 1)}% {changeLabel}
        </span>
      )}
      {trailing}
    </>
  );
  const rowClassName = 'flex items-end justify-between gap-4';

  return to ? (
    // The negative margin cancels the hover padding, so the number stays aligned with the card's content.
    <Link to={to} className={cn(rowClassName, '-m-2 rounded-md p-2 hover:bg-muted', className)}>
      {content}
    </Link>
  ) : (
    <div className={cn(rowClassName, className)}>{content}</div>
  );
};
